import {
  createFileRoute,
  redirect,
  useNavigate,
  useRouteContext,
} from "@tanstack/react-router";
import { getDefaultStore, useAtomValue, useSetAtom } from "jotai";
import type { JSX } from "react";

import { graphql } from "@repo/graphql-types/gql";
import {
  CalendarPageAppointmentsQueryDocument,
  CalendarPageFreeSlotsQueryDocument,
  WaitingRoomPageQueryDocument,
} from "@repo/graphql-types/graphql";

import { HighCancellationAlert } from "@/components/appointment/high-cancellation-alert";
import { BatchPatientName } from "@/components/batch-adjust/batch-patient-name";
import { BatchRecurringNotice } from "@/components/batch-adjust/batch-recurring-notice";
import { RecurrenceCreateHeader } from "@/components/recurring-appointment/recurrence-create-header";
import { Button } from "@/components/ui/button/button";
import { Page } from "@/components/ui/layout/page/page";
import { RoundedLayout } from "@/components/ui/layout/rounded-layout";
import {
  useGraphQLMutationWithErrorHandler,
  useInvalidateQuery,
} from "@/hooks/use-graphql";
import {
  type BatchAdjustTarget,
  batchAdjustAppliedIdsAtom,
  batchAdjustOriginAtom,
  batchAdjustTargetsAtom,
} from "@/lib/atoms/batch-adjust-form-atom";
import { toDisplayDate, toDisplayTime } from "@/lib/helpers/batch-adjust";
import { trackBatchAppointmentsChanged } from "@/lib/helpers/batch-adjust-tracking";

const BatchCancelMutation = graphql(/* GraphQL */ `
  mutation BatchCancelMutation($input: RemarcarAgendamentosEmBatchInput!) {
    remarcarAgendamentosEmBatch(input: $input) {
      data: remarcarEmBatchOutput {
        sucessos {
          codAgendamentoOrigem
        }
      }
      errors {
        ... on ValidationError {
          __typename
          message
        }
      }
    }
  }
`);

interface BatchCancelItemProps {
  target: BatchAdjustTarget;
}

const BatchCancelItem = ({ target }: BatchCancelItemProps): JSX.Element => (
  <div className="flex flex-col gap-3">
    <div className="flex min-w-0 flex-col">
      <BatchPatientName
        nomePaciente={target.nomePaciente}
        recorrente={target.recorrente}
      />
      <span className="text-neutral-500 text-xs">
        {toDisplayTime(target.horaInicio)} - {toDisplayTime(target.horaFim)} ·{" "}
        {target.nomeUnidade}
      </span>
    </div>

    {target.recorrente ? (
      <BatchRecurringNotice
        data={target.data}
        horaInicio={target.horaInicio}
        horaFim={target.horaFim}
        action="cancel"
      />
    ) : null}
  </div>
);

const cancelLabelFor = (count: number): string =>
  count === 1 ? "Cancelar 1 agendamento" : `Cancelar ${count} agendamentos`;

const notifyNoticeFor = (count: number): string =>
  count === 1
    ? "O paciente será avisado por e-mail sobre o cancelamento."
    : "Os pacientes serão avisados por e-mail sobre o cancelamento.";

const BatchCancelPage = (): JSX.Element => {
  const navigate = useNavigate();
  const { user } = useRouteContext({ from: "__root__" });
  const targets = useAtomValue(batchAdjustTargetsAtom);
  const origin = useAtomValue(batchAdjustOriginAtom);
  const setAppliedIds = useSetAtom(batchAdjustAppliedIdsAtom);

  const { mutate, isPending } = useGraphQLMutationWithErrorHandler(BatchCancelMutation, {
    retry: 0,
  });
  const invalidateCalendarAppointments = useInvalidateQuery(
    CalendarPageAppointmentsQueryDocument,
  );
  const invalidateCalendarFreeSlots = useInvalidateQuery(
    CalendarPageFreeSlotsQueryDocument,
  );
  const invalidateWaitingRoom = useInvalidateQuery(WaitingRoomPageQueryDocument);

  const handleConfirm = (): void => {
    const agendamentos = targets.map((target) => ({
      codAgendamento: target.codAgendamento,
      cancelado: true,
      ignorarEnvioEmail: false,
    }));

    mutate(
      { input: { agendamentos } },
      {
        onSuccess: () => {
          invalidateCalendarAppointments();
          invalidateCalendarFreeSlots();
          invalidateWaitingRoom();
          setAppliedIds(targets.map((target) => target.codAgendamento));
          trackBatchAppointmentsChanged({
            codAgendamentos: targets.map((target) => target.codAgendamento),
            totalSelecionado: targets.length,
            acao: "cancelado",
            origin,
          });
          void navigate({
            to: "/calendar/batch-adjust/success",
            search: { action: "cancel", count: targets.length },
          });
        },
      },
    );
  };

  return (
    <RoundedLayout className="-mb-[env(safe-area-inset-bottom)]">
      <RecurrenceCreateHeader
        title="Cancelar agendamentos"
        subtitle={targets.length > 0 ? toDisplayDate(targets[0].data) : ""}
      />
      <Page className="flex flex-col">
        <div className="flex h-full flex-col justify-between gap-4">
          <div className="flex flex-col gap-4">
            <div className="rounded-lg border-[1.5px] border-neutral-100 p-4">
              <div className="flex flex-col divide-y divide-neutral-100 *:py-2.5 [&>*:first-child]:pt-0 [&>*:last-child]:pb-0">
                {targets.map((target) => (
                  <BatchCancelItem key={target.codAgendamento} target={target} />
                ))}
              </div>
            </div>

            <p className="text-neutral-600 text-sm">{notifyNoticeFor(targets.length)}</p>

            {user.altaTaxaCancelamento ? <HighCancellationAlert /> : null}
          </div>

          <div className="sticky bottom-0 bg-white pt-2 pb-2">
            <Button
              fullWidth
              loading={isPending}
              disabled={isPending || targets.length === 0}
              onClick={handleConfirm}
            >
              {cancelLabelFor(targets.length)}
            </Button>
          </div>
        </div>
      </Page>
    </RoundedLayout>
  );
};

export const Route = createFileRoute("/calendar/batch-adjust/cancel")({
  component: BatchCancelPage,
  beforeLoad: ({ context }) => {
    if (!context.flags["ajuste-em-lote-de-agendamentos"]) {
      redirect({ to: "/calendar", throw: true });
    }
    if (getDefaultStore().get(batchAdjustTargetsAtom).length === 0) {
      redirect({ to: "/calendar", throw: true });
    }
  },
});
